import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  IconButton,
  LinearProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ALLOWED_MIME_TYPES, DEFAULT_MAX_FILE_SIZE_BYTES, getAcceptAttribute } from '../utils';
import ListingPickerDialog from './ListingPickerDialog';
import FilePreviewCard from './FilePreviewCard';

const MultiUploadDialog = React.forwardRef(
  (
    {
      open,
      config,
      cottages,
      counts,
      maxCount,
      isUploading,
      uploadProgress,
      uploadFileIndex,
      uploadFileCount,
      onUpload,
      onClose,
    },
    ref,
  ) => {
    const [step, setStep] = React.useState(1);
    const [selectedListingIds, setSelectedListingIds] = React.useState([]);
    const [selectedFiles, setSelectedFiles] = React.useState([]);
    const [skippedTypes, setSkippedTypes] = React.useState(false);
    const [skippedSize, setSkippedSize] = React.useState(false);
    const [previewUrls, setPreviewUrls] = React.useState({});
    const fileInputRef = React.useRef(null);
    const prevUploading = React.useRef(false);

    const singleCottage = cottages.length === 1;

    const getListingCount = (cottageId) =>
      (counts && counts.listings && counts.listings[cottageId]) || 0;

    const getMaxFiles = (listingIds) => {
      if (!listingIds || listingIds.length === 0) return 0;
      return Math.max(0, Math.min(...listingIds.map((id) => maxCount - getListingCount(id))));
    };

    // Reset state when dialog opens/closes
    React.useEffect(() => {
      if (open) {
        setSelectedFiles([]);
        setSkippedTypes(false);
        setSkippedSize(false);
        setStep(1);
      }
    }, [open]);

    // Keep single-cottage selection in sync if cottages load after the dialog
    // opens, or if the cottage list changes while open.
    React.useEffect(() => {
      if (!open) return;
      if (singleCottage) {
        setSelectedListingIds([cottages[0].id]);
      } else {
        setSelectedListingIds([]);
      }
    }, [open, singleCottage, cottages]);

    // Expose imperative openPicker for single-cottage callers.
    // Avoids useEffect-based auto-open, which doesn't re-fire when
    // `open` is already true (native picker cancel isn't reliably
    // observable in React 17 + older browsers).
    React.useImperativeHandle(
      ref,
      () => ({
        openPicker: () => {
          if (singleCottage && getMaxFiles([cottages[0].id]) > 0) {
            fileInputRef.current?.click();
          }
        },
      }),
      [singleCottage, cottages, counts, maxCount],
    );

    // Generate/revoke preview URLs for image files
    React.useEffect(() => {
      const urls = {};
      selectedFiles.forEach((file, i) => {
        if (file.type && file.type.startsWith('image/')) {
          urls[i] = URL.createObjectURL(file);
        }
      });
      setPreviewUrls(urls);
      return () => Object.values(urls).forEach((u) => URL.revokeObjectURL(u));
    }, [selectedFiles]);

    // Close dialog when upload finishes
    React.useEffect(() => {
      if (prevUploading.current && !isUploading) {
        onClose();
      }
      prevUploading.current = isUploading;
    }, [isUploading, onClose]);

    const handleChooseFilesClick = (listingIds) => {
      setSelectedListingIds(listingIds);
      fileInputRef.current.click();
    };

    const handleFilesSelected = (e) => {
      const fileList = Array.from(e.target.files);
      if (!fileList.length) return;

      const allowedTypes = config?.allowed_mime_types || ALLOWED_MIME_TYPES;
      const maxSize = config?.max_file_size_bytes || DEFAULT_MAX_FILE_SIZE_BYTES;

      // Filter valid files, track why files were skipped
      const hadBadType = fileList.some((f) => !allowedTypes.includes(f.type));
      const hadTooLarge = fileList.some((f) => f.size > maxSize);
      const valid = fileList.filter((f) => allowedTypes.includes(f.type) && f.size <= maxSize);

      setSkippedTypes(hadBadType);
      setSkippedSize(hadTooLarge);

      // Reset so same files can be re-selected even if all were rejected.
      e.target.value = '';

      if (valid.length === 0) return;

      setSelectedFiles(valid);
      setStep(2);
    };

    const handleRemoveFile = (index) => {
      const next = selectedFiles.filter((_, i) => i !== index);
      if (next.length === 0) {
        setStep(1);
      }
      setSelectedFiles(next);
    };

    const handleBack = () => {
      if (singleCottage) {
        onClose();
      } else {
        setSelectedFiles([]);
        setSkippedTypes(false);
        setSkippedSize(false);
        setStep(1);
      }
    };

    const handleUpload = () => {
      onUpload({ files: selectedFiles, listing_ids: selectedListingIds });
    };

    const maxFiles = getMaxFiles(selectedListingIds);

    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={getAcceptAttribute(config?.allowed_mime_types)}
          style={{ display: 'none' }}
          onChange={handleFilesSelected}
        />
        {step === 1 && singleCottage && maxFiles <= 0 && (
          <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
              <FormattedMessage id="hostFiles.uploadTitle" defaultMessage="Upload files" />
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="error">
                <FormattedMessage
                  id="hostFiles.maxFilesWarning"
                  defaultMessage="Max {max} files can be selected"
                  values={{ max: maxCount }}
                />
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                <FormattedMessage id="Close" defaultMessage="Close" />
              </Button>
            </DialogActions>
          </Dialog>
        )}
        {step === 1 && !singleCottage && (
          <ListingPickerDialog
            open={open}
            cottages={cottages}
            counts={counts}
            maxCount={maxCount}
            file={null}
            onClose={onClose}
            actions={(listingIds) => (
              <>
                <Button onClick={onClose}>
                  <FormattedMessage id="Cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  color="primary"
                  disabled={listingIds.length === 0 || getMaxFiles(listingIds) <= 0}
                  onClick={() => handleChooseFilesClick(listingIds)}
                >
                  <FormattedMessage
                    id="hostFiles.chooseFiles"
                    defaultMessage="Choose files (max {max})"
                    values={{ max: getMaxFiles(listingIds) }}
                  />
                </Button>
              </>
            )}
          />
        )}
        {step === 2 && (
          <Dialog open={open} onClose={isUploading ? undefined : onClose} maxWidth="sm" fullWidth>
            {isUploading && (
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                style={{ position: 'absolute', left: 0, right: 0, top: 0 }}
              />
            )}
            <DialogTitle>
              {isUploading ? (
                <FormattedMessage
                  id="hostFiles.uploadingFile"
                  defaultMessage="Uploading file {current} of {total}..."
                  values={{ current: uploadFileIndex + 1, total: uploadFileCount }}
                />
              ) : (
                <FormattedMessage id="hostFiles.uploadTitle" defaultMessage="Upload files" />
              )}
            </DialogTitle>
            <DialogContent>
              {selectedFiles.length > maxFiles && (
                <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
                  <FormattedMessage
                    id="hostFiles.maxFilesWarning"
                    defaultMessage="Max {max} files can be selected"
                    values={{ max: maxFiles }}
                  />
                </Typography>
              )}
              {skippedTypes && (
                <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
                  <FormattedMessage
                    id="hostFiles.errorInvalidType"
                    defaultMessage="This file type is not allowed."
                  />
                </Typography>
              )}
              {skippedSize && (
                <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
                  <FormattedMessage
                    id="hostFiles.errorTooLarge"
                    defaultMessage="This file is too large."
                  />
                </Typography>
              )}
              {selectedFiles.map((file, index) => (
                <FilePreviewCard
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  name={file.name}
                  mimeType={file.type}
                  sizeBytes={file.size}
                  previewUrl={previewUrls[index]}
                >
                  <IconButton
                    size="small"
                    disabled={isUploading}
                    onClick={() => handleRemoveFile(index)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </FilePreviewCard>
              ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleBack} disabled={isUploading}>
                <FormattedMessage id="hostFiles.back" defaultMessage="Back" />
              </Button>
              <Button
                color="primary"
                disabled={
                  isUploading || selectedFiles.length === 0 || selectedFiles.length > maxFiles
                }
                onClick={handleUpload}
              >
                {selectedFiles.length > 1 ? (
                  <FormattedMessage id="hostFiles.uploadFiles" defaultMessage="Upload files" />
                ) : (
                  <FormattedMessage id="hostFiles.upload" defaultMessage="Upload file" />
                )}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </>
    );
  },
);

MultiUploadDialog.displayName = 'MultiUploadDialog';

MultiUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  config: PropTypes.shape({
    allowed_mime_types: PropTypes.arrayOf(PropTypes.string),
    max_file_size_bytes: PropTypes.number,
    max_files_per_slot: PropTypes.number,
  }),
  cottages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title_sv: PropTypes.string,
    }),
  ).isRequired,
  counts: PropTypes.shape({
    listings: PropTypes.shape({}),
  }),
  maxCount: PropTypes.number.isRequired,
  isUploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  uploadFileIndex: PropTypes.number,
  uploadFileCount: PropTypes.number,
  onUpload: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MultiUploadDialog;
